<template>
  <div id="collaborators">
    <b-card
      class="blockDrawer large"
      :class="$store.state.drawer.collaborators.position"
    >
      <template v-slot:header>
        <a
          class="close clickable"
          @click="closeCollaborators()"
        >
          <i class="far fa-times"></i>
        </a>
        <h4 class="card-title">Farm Collaborators</h4>
      </template>

      <p v-if="$store.state.farm.ownerData">
        <strong>Farm Owner:</strong> {{ $store.state.farm.ownerData.email }}
      </p>

      <!-- INVITE FORM -->
      <div class="card mb-3">
        <div class="card-body p-3">
          <h6 class="mb-2">Invite a collaborator</h6>
          <form @submit.prevent="invite">
            <div class="form-row">
              <div class="col-12 col-md-6 mb-2">
                <input
                  v-model="invitation.email"
                  type="email"
                  class="form-control form-control-sm"
                  placeholder="email@example.com"
                  required
                  autocomplete="off"
                />
              </div>
              <div class="col-7 col-md-4 mb-2">
                <b-form-select
                  v-model="invitation.role"
                  :options="permissionOptions"
                  size="sm"
                />
              </div>
              <div class="col-5 col-md-2 mb-2">
                <b-btn
                  type="submit"
                  variant="primary"
                  size="sm"
                  block
                  :disabled="!invitation.email || inviting"
                >
                  <i class="fas fa-paper-plane" v-if="!inviting"></i>
                  <i class="fas fa-spinner fa-spin" v-else></i>
                  Invite
                </b-btn>
              </div>
            </div>
          </form>
          <small class="text-muted">
            The invitee gets an email with a link to accept or decline.
            If they don't have an Every.Farm account yet, signing up
            with this email automatically gives them access.
          </small>
        </div>
      </div>

      <!-- PENDING INVITES -->
      <div v-if="loadingInvites" class="text-muted small py-2">
        <i class="fas fa-spinner fa-spin"></i> Loading pending invitations…
      </div>
      <div v-else-if="pendingInvites.length > 0" class="mb-3">
        <h6 class="mb-2">Pending invitations ({{ pendingInvites.length }})</h6>
        <table class="table table-sm table-hover mb-0">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Sent</th>
              <th scope="col" class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inv in pendingInvites" :key="inv.id">
              <td>
                {{ inv.email }}
                <br>
                <small class="text-muted">
                  Invited by {{ inv.invitedByEmail || 'unknown' }}
                </small>
              </td>
              <td>
                <span class="badge badge-light">{{ inv.role }}</span>
              </td>
              <td>
                <span v-b-tooltip.hover :title="formatDate(inv.lastEmailSentAt)">
                  {{ formatRelative(inv.lastEmailSentAt) }}
                </span>
                <br>
                <small v-if="inv.resendCount > 0" class="text-muted">
                  resent {{ inv.resendCount }}×
                </small>
              </td>
              <td class="text-right">
                <b-btn
                  size="sm"
                  variant="outline-secondary"
                  class="mr-1"
                  :disabled="actingInviteId === inv.id"
                  @click="resend(inv)"
                >
                  <i class="fas fa-redo"></i> Resend
                </b-btn>
                <b-btn
                  size="sm"
                  variant="outline-danger"
                  :disabled="actingInviteId === inv.id"
                  @click="cancel(inv)"
                >
                  <i class="fas fa-times"></i> Cancel
                </b-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ACTIVE COLLABORATORS -->
      <table
        v-if="$store.state.farm.collaboratorData && $store.state.farm.users.length > 0"
        class="table table-striped table-hover mt-3"
      >
        <thead>
          <tr>
            <th scope="col">Email</th>
            <th scope="col">Role</th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(collaborator, index) in $store.state.farm.collaboratorData"
            v-bind:key="index"
          >
            <td>{{ collaborator.email }}</td>
            <td>
              <b-form-select
                v-model="farm.permissions[collaborator.id]"
                :options="permissionOptions"
                size="sm"
              />
            </td>
            <td>
              <i
                class="fal fa-user-slash clickable"
                v-b-tooltip.hover
                title="Remove Collaborator"
                @click="confirmRemove = { confirm: true, index: index, userId: collaborator.id }"
              ></i>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="float-right mt-2">
        <button
          v-if="$store.state.farm.collaboratorData && $store.state.farm.users.length > 0"
          @click="savePermissions()"
          class="btn btn-primary mr-1"
        >Save Permissions</button>
        <button
          @click="closeCollaborators()"
          class="btn btn-secondary"
        >
          Cancel
        </button>
      </div>
    </b-card>
    <Confirm
      v-if="confirmRemove.confirm"
      text="Are you sure you want to remove this user from this farm?"
      v-on:confirmed="removeCollaborator($event)"
    />
  </div><!-- /#collaborators -->
</template>

<script>
import Confirm from '../Confirm'
const fb = require('../../firebaseConfig.js')
// Direct-fetch wrapper for callables — works around the v7 namespaced
// firebase JS SDK's intermittent `code: 'internal'` failure mode that
// throws before the network request even leaves the browser. Same
// workaround already used by Admin.vue and store/mapActions.js.
const { callCallable } = require('../../lib/callable.js')

// Default role for new invites. Editor is the most useful general-
// purpose role — the inviter can downgrade to Read-only inline.
const DEFAULT_INVITE_ROLE = 'Editor'

export default {
  data () {
    return {
      confirmRemove: { confirm: false, index: 0, userId: '' },
      permissionOptions: ['Permission-only', 'Read-only', 'Editor', 'Administrator'],
      invitation: { email: '', role: DEFAULT_INVITE_ROLE },
      inviting: false,
      loadingInvites: true,
      pendingInvites: [],
      actingInviteId: null,
      invitesUnsub: null
    }
  },
  components: {
    Confirm
  },
  computed: {
    farm: {
      get () {
        return this.$store.state.farm
      },
      set (value) {
        this.$store.commit('setFarm', value)
      }
    }
  },
  created () {
    this.subscribeInvites()
  },
  beforeDestroy () {
    if (this.invitesUnsub) {
      this.invitesUnsub()
      this.invitesUnsub = null
    }
  },
  methods: {
    closeCollaborators () {
      this.$store.commit('hideDrawer', 'collaborators')
      const self = this
      setTimeout(function () {
        self.$store.commit('showDrawer', 'farmDetails')
      }, 300)
    },

    // Live-subscribe to pending invites for this farm that the
    // current user sent. The Firestore rule limits reads to invites
    // where `invitedByUid == request.auth.uid` (or addressed to the
    // user's own email), so the query MUST include the same
    // constraint — otherwise the rules engine rejects the whole
    // query and we'd silently get an empty list. Each admin sees
    // the invites they themselves issued. Showing every admin's
    // outgoing invites in one list is a follow-up that needs a
    // listPendingInvites callable to do the role check server-side.
    subscribeInvites () {
      const farmId = this.farm && this.farm.id
      const uid = fb.auth.currentUser && fb.auth.currentUser.uid
      if (!farmId || !uid) {
        this.loadingInvites = false
        return
      }
      this.loadingInvites = true
      this.invitesUnsub = fb.db.collection('invitations')
        .where('farmId', '==', farmId)
        .where('invitedByUid', '==', uid)
        .where('status', '==', 'pending')
        .onSnapshot(snap => {
          this.pendingInvites = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              const ad = this.toDate(a.lastEmailSentAt) || new Date(0)
              const bd = this.toDate(b.lastEmailSentAt) || new Date(0)
              return bd - ad
            })
          this.loadingInvites = false
        }, err => {
          console.log('invitations subscribe error:', err && err.message)
          this.loadingInvites = false
        })
    },

    async invite () {
      const email = (this.invitation.email || '').trim().toLowerCase()
      if (!email) return
      this.inviting = true
      try {
        const data = await callCallable('inviteCollaborator', {
          farmId: this.farm.id,
          email,
          role: this.invitation.role || DEFAULT_INVITE_ROLE
        })
        if (data && data.alreadyMember) {
          this.$store.commit('setAlert', {
            active: true,
            message: data.message || 'That user is already a member of this farm.',
            level: 'alert-info',
            timeout: 5000
          })
        } else {
          this.$store.commit('setAlert', {
            active: true,
            message: 'Invitation sent. They\'ll receive an email with a link to accept.',
            level: 'alert-success',
            timeout: 5000
          })
        }
        this.invitation.email = ''
        this.invitation.role = DEFAULT_INVITE_ROLE
      } catch (err) {
        console.log('invite error:', err)
        this.$store.commit('setAlert', {
          active: true,
          message: (err && err.message) || 'Could not send invitation.',
          level: 'alert-warning',
          timeout: 6000
        })
      }
      this.inviting = false
    },

    async resend (invite) {
      this.actingInviteId = invite.id
      try {
        await callCallable('resendInvitation', { inviteId: invite.id })
        this.$store.commit('setAlert', {
          active: true,
          message: 'Invitation email resent.',
          level: 'alert-success',
          timeout: 4000
        })
      } catch (err) {
        this.$store.commit('setAlert', {
          active: true,
          message: (err && err.message) || 'Could not resend invitation.',
          level: 'alert-warning',
          timeout: 6000
        })
      }
      this.actingInviteId = null
    },

    async cancel (invite) {
      this.actingInviteId = invite.id
      try {
        await callCallable('cancelInvitation', { inviteId: invite.id })
        this.$store.commit('setAlert', {
          active: true,
          message: 'Invitation cancelled.',
          level: 'alert-success',
          timeout: 4000
        })
      } catch (err) {
        this.$store.commit('setAlert', {
          active: true,
          message: (err && err.message) || 'Could not cancel invitation.',
          level: 'alert-warning',
          timeout: 6000
        })
      }
      this.actingInviteId = null
    },

    savePermissions () {
      const self = this
      fb.farmsCollection.doc(self.farm.id).update({
        permissions: self.farm.permissions,
        updatedOn: new Date()
      }).then(() => {
        this.closeCollaborators()
      }).catch(err => {
        console.log(err)
      })
    },

    removeCollaborator (confirmed) {
      const index = this.confirmRemove.index
      const userId = this.confirmRemove.userId
      const self = this
      if (confirmed) {
        self.confirmRemove.confirm = false
        self.farm.users.splice(index, 1)
        // Best-effort notification to the removed user — sent BEFORE the
        // permission is revoked: processNotification only emails recipients
        // who are members of the notification's farm (anti-phishing gate),
        // so this must be created while the user still holds a role.
        fb.notificationsCollection.add({
          createdOn: new Date(),
          updatedOn: new Date(),
          createdBy: self.$store.state.currentUser.uid,
          farmId: self.$store.state.farm.id,
          subject: 'Every.Farm collaboration ended',
          message: `Your access to "${self.$store.state.farm.name}" on Every.Farm has been removed.`,
          recipient: userId,
          url: 'https://app.every.farm/',
          read: false
        }).catch(() => { /* best-effort */ })
        delete self.farm.permissions[userId]
        fb.farmsCollection.doc(self.farm.id).update({
          users: self.farm.users,
          permissions: self.farm.permissions,
          updatedOn: new Date()
        }).then(() => {
          self.$store.dispatch('fetchFarm', self.farm.id)
        })
      } else {
        self.confirmRemove.confirm = false
        return false
      }
    },

    // --- timestamp helpers (identical to ApiTokens.vue) -------------
    toDate (timestamp) {
      if (!timestamp) return null
      if (timestamp instanceof Date) return timestamp
      if (typeof timestamp === 'number') return new Date(timestamp)
      if (typeof timestamp === 'string') {
        const d = new Date(timestamp)
        return isNaN(d.getTime()) ? null : d
      }
      if (typeof timestamp === 'object') {
        if (typeof timestamp.toDate === 'function') {
          try { return timestamp.toDate() } catch (e) { /* fall through */ }
        }
        if (typeof timestamp._seconds === 'number') {
          return new Date(timestamp._seconds * 1000)
        }
        if (typeof timestamp.seconds === 'number') {
          return new Date(timestamp.seconds * 1000)
        }
      }
      return null
    },
    formatDate (timestamp) {
      const date = this.toDate(timestamp)
      if (!date) return ''
      return date.toLocaleString()
    },
    formatRelative (timestamp) {
      const date = this.toDate(timestamp)
      if (!date) return ''
      const diffMs = Date.now() - date.getTime()
      const sec = Math.round(diffMs / 1000)
      if (sec < 45) return 'just now'
      if (sec < 90) return 'a minute ago'
      const min = Math.round(sec / 60)
      if (min < 45) return `${min} minutes ago`
      if (min < 90) return 'an hour ago'
      const hr = Math.round(min / 60)
      if (hr < 24) return `${hr} hours ago`
      if (hr < 36) return 'yesterday'
      const day = Math.round(hr / 24)
      if (day < 30) return `${day} days ago`
      if (day < 60) return 'last month'
      const mo = Math.round(day / 30)
      if (mo < 12) return `${mo} months ago`
      const yr = Math.round(day / 365)
      return yr === 1 ? 'a year ago' : `${yr} years ago`
    }
  }
}
</script>
